import {
  SCHEMA_VERSION,
  SCHEMA_VERSION_KEY,
  DEMO_IDS,
  emptyCollections,
  createUser,
  createTransaction,
  createThread,
  createMessage,
  createAuditLog,
  nowIso,
} from "./schema";
import { getSnapshot, invalidateDbCache, replaceCollections } from "./engine";

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function upsertById(list, record) {
  const index = list.findIndex((row) => row.id === record.id);
  if (index === -1) list.push(record);
  else {
    list[index] = {
      ...list[index],
      ...record,
      id: record.id,
      created_at: list[index].created_at,
    };
  }
}

function upsertUser(collections, input) {
  const email = String(input.email || "").toLowerCase();
  const existing =
    collections.users.find((row) => row.id === input.id) ||
    (email ? collections.users.find((row) => row.email === email) : null);
  const user = createUser({
    ...existing,
    ...input,
    id: input.id || existing?.id,
    email: email || existing?.email,
    created_at: existing?.created_at,
    betaTester: input.betaTester ?? existing?.betaTester,
    betaAccepted: input.betaAccepted ?? existing?.betaAccepted,
    betaRole: input.betaRole ?? existing?.betaRole,
    betaAcceptedAt: input.betaAcceptedAt ?? existing?.betaAcceptedAt,
    phone: input.phone ?? existing?.phone,
    license_number: input.license_number ?? existing?.license_number,
    first_name: input.first_name ?? existing?.first_name,
    last_name: input.last_name ?? existing?.last_name,
  });
  const clash = collections.users.find((row) => row.email && row.email === user.email && row.id !== user.id);
  if (clash) {
    collections.users = collections.users.filter((row) => row.id !== clash.id);
  }
  upsertById(collections.users, user);
}

export function syncIdentities(collections = getSnapshot().collections) {
  const buyers = readJson("dh_buyers", []);
  const agents = readJson("dh_agents", []);

  buyers.forEach((buyer) => {
    upsertUser(collections, {
      id: buyer.id,
      role_type: "buyer",
      name: buyer.name,
      email: buyer.email,
      status: buyer.status === "inactive" ? "inactive" : "active",
    });
  });

  agents.forEach((agent) => {
    upsertUser(collections, {
      id: agent.id,
      role_type: agent.role === "admin" ? "broker" : "agent",
      name: agent.name,
      email: agent.email,
      status: "active",
      brokerage_id: DEMO_IDS.broker,
    });
  });

  return collections;
}

function importLegacyMessages(collections) {
  const legacy = readJson("dh_messages", []);
  if (!legacy.length) return;

  const pairs = new Map();
  legacy.forEach((item) => {
    const agentId = item.agentId || DEMO_IDS.agent;
    const clientId = item.buyerId || DEMO_IDS.buyer;
    const key = `${agentId}::${clientId}`;
    if (!pairs.has(key)) pairs.set(key, { agentId, clientId, messages: [] });
    pairs.get(key).messages.push(item);
  });

  pairs.forEach(({ agentId, clientId, messages }) => {
    if (!collections.users.some((row) => row.id === agentId)) {
      upsertUser(collections, {
        id: agentId,
        role_type: "agent",
        name: "Agent",
        email: `${agentId}@dreamhome.local`,
        brokerage_id: DEMO_IDS.broker,
      });
    }
    if (!collections.users.some((row) => row.id === clientId)) {
      upsertUser(collections, {
        id: clientId,
        role_type: "buyer",
        name: "Buyer",
        email: `${clientId}@dreamhome.local`,
      });
    }

    const txId = clientId === DEMO_IDS.buyer ? DEMO_IDS.txBuyer : `tx-${clientId}`;
    if (!collections.transactions.some((row) => row.id === txId)) {
      collections.transactions.push(
        createTransaction({
          id: txId,
          broker_id: DEMO_IDS.broker,
          agent_id: agentId,
          client_id: clientId,
          client_role: "buyer",
          property_address: "DreamHome Journey — active search",
          status: "open",
        })
      );
    }

    const threadId = clientId === DEMO_IDS.buyer ? DEMO_IDS.threadBuyer : `thread-${clientId}`;
    if (!collections.message_threads.some((row) => row.id === threadId)) {
      collections.message_threads.push(
        createThread({
          id: threadId,
          transaction_id: txId,
          created_by_user_id: agentId,
        })
      );
    }

    messages.forEach((item) => {
      if (collections.messages.some((row) => row.id === item.id)) return;
      const fromAgent = item.sender === "agent";
      collections.messages.push(
        createMessage({
          id: item.id,
          thread_id: threadId,
          sender_id: fromAgent ? agentId : clientId,
          receiver_id: fromAgent ? clientId : agentId,
          message_text: item.content || item.message_text || "",
          timestamp: item.createdAt || item.timestamp || nowIso(),
          read_status: Boolean(item.read ?? item.read_status),
        })
      );
    });
  });
}

function runV1() {
  const collections = emptyCollections();
  syncIdentities(collections);
  upsertUser(collections, {
    id: DEMO_IDS.broker,
    role_type: "broker",
    name: "Morgan Ellison",
    email: "broker@dreamhome.local",
    status: "active",
  });
  importLegacyMessages(collections);
  collections.audit_logs.push(
    createAuditLog({
      user_id: DEMO_IDS.broker,
      action_type: "schema.migrate",
      metadata: { version: SCHEMA_VERSION, source: "dh_messages" },
    })
  );
  replaceCollections(collections);
  localStorage.setItem(SCHEMA_VERSION_KEY, String(SCHEMA_VERSION));
}

export function migrateJourneyDb() {
  const stored = Number(localStorage.getItem(SCHEMA_VERSION_KEY) || 0);
  if (stored < 1) {
    runV1();
    return;
  }
  invalidateDbCache();
  const { collections } = getSnapshot();
  if (!Array.isArray(collections.beta_feedback)) collections.beta_feedback = [];
  syncIdentities(collections);
  replaceCollections(collections);
  if (stored < SCHEMA_VERSION) {
    localStorage.setItem(SCHEMA_VERSION_KEY, String(SCHEMA_VERSION));
  }
}
